<template>
  <div>
    <nav-drawer
      v-if="isNarrow"
      :value="value"
      @input="$emit('input', $event)"
      :sections="sections"
      :starred-pages="starredPages"
      :recent-pages="recentPages"
      :location="location"
    >
      <template v-slot:account="{parts}">
        <slot name="account" :parts="parts"/>
      </template>
    </nav-drawer>

    <!-- a panel of our own rather than a v-menu, which sizes itself once when it opens and so
         can't grow as columns open; clicking anywhere else closes it -->
    <template v-else-if="value">
      <div class="nav-backdrop d-print-none" @click="close"/>
      <div class="nav-panel d-print-none">
        <nav-columns
          :sections="sections"
          :starred-pages="starredPages"
          :recent-pages="recentPages"
          :starred="starred"
          :location="location"
          :editing="editing"
          @move="startMove"
          @edit-section="startEditSection"
          @edit-page="startEditPage"
          @star="star"
        >
          <template v-slot:actions>
            <template v-if="editing">
              <v-btn
                v-if="hasChanges"
                icon
                small
                title="reset the menu: put every page and section back as it started; your stars and recent pages stay"
                data-cy="nav-reset"
                @click="resetMenu"
              >
                <v-icon small>mdi-restore</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                title="download your menu settings as json"
                data-cy="nav-download"
                @click="downloadSettings"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </template>
            <v-btn
              v-if="canEdit"
              icon
              small
              :color="editing ? 'primary' : undefined"
              :title="editing ? 'done editing the menu' : 'edit the menu: move pages, rename pages and sections, make your own sections'"
              data-cy="nav-edit"
              @click="editing = !editing"
            >
              <v-icon small>{{ editing ? 'mdi-check' : 'mdi-pencil' }}</v-icon>
            </v-btn>
          </template>
          <template v-slot:footer>
            <v-btn
              text
              x-small
              title="go back to the old menu: one list of every page"
              data-cy="nav-old-menu"
              @click="$emit('use-old-menu')"
            >
              <v-icon left x-small>mdi-undo</v-icon>
              old menu
            </v-btn>
          </template>
          <template v-slot:account="{parts}">
            <slot name="account" :parts="parts"/>
          </template>
        </nav-columns>
      </div>
    </template>

    <v-dialog :value="!!moving" max-width="560" @input="moving = null">
      <v-card v-if="moving" data-cy="nav-move-dialog">
        <v-card-title>Move {{ movingTitle }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="moveTarget"
            :items="targets"
            label="put it in"
            autofocus
            data-cy="nav-move-target"
            @change="newSectionTitle = ''"
          />
          <v-text-field
            v-model="newSectionTitle"
            label="or a new section"
            data-cy="nav-move-new-section"
            @input="moveTarget = null"
          />
          This only changes your menu, in this browser.
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="moves[moving.name]" text @click="putBack">put back</v-btn>
          <v-spacer/>
          <v-btn text @click="moving = null">cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!moveTarget && !newSectionTitle.trim()"
            data-cy="nav-move-save"
            @click="saveMove"
          >move</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!editingText" max-width="460" @input="editingText = null">
      <v-card v-if="editingText" data-cy="nav-text-dialog">
        <v-card-title>Edit {{ editingText.title }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editedTitle"
            label="name"
            autofocus
            data-cy="nav-text-title"
            @keydown.enter="saveText"
          />
          <v-text-field
            v-model="editedSummary"
            :label="editingText.kind === 'section' ? 'subtitle' : 'description'"
            data-cy="nav-text-summary"
            @keydown.enter="saveText"
          />
          Leave one empty to go back to how it started.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="editingText = null">cancel</v-btn>
          <v-btn color="primary" data-cy="nav-text-save" @click="saveText">save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NavColumns from '@/components/NavColumns.vue';
import NavDrawer from '@/components/NavDrawer.vue';
import {navSections} from '@/nav';
import {
  applyMenuSettings, filterNav, findItem, isStarred, moveTargets, pageTitle, rememberPage, sectionId, toggleStar,
  withoutIdPages, hasFewPages,
} from '@/nav-helpers';
import {loadNavSettings, saveNavSettings} from '@/nav-settings';
import {downloadBlob} from '@/utils/download';

/**
 * The menu, of the pages the user's role can open: columns on a wide screen, a drawer on a
 * narrow one. Keeps the user's own settings in this browser: pages they have moved, sections
 * they renamed or made, pages they starred and the last pages they opened.
 */
export default {
  name: 'NavMenu',
  components: {NavColumns, NavDrawer},
  props: {
    value: {type: Boolean, required: true},
    /** turns a nav item into a router location (adds the current dates/production) */
    location: {type: Function, required: true},
    /** route names the user's role can open */
    pages: {type: Array, required: true},
  },
  data() {
    const {moves, sectionNames, sectionSummaries, pageNames, pageSummaries, customSections, starred, recent} = loadNavSettings();
    return {
      moves,
      sectionNames,
      sectionSummaries,
      pageNames,
      pageSummaries,
      customSections,
      starred,
      recent,
      editing: false,
      moving: null,
      moveTarget: null,
      newSectionTitle: '',
      /** the section or page whose name and description are being edited: {kind, key, title} */
      editingText: null,
      editedTitle: '',
      editedSummary: '',
    };
  },
  mounted() {
    document.addEventListener('keydown', this.closeOnEscape);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.closeOnEscape);
  },
  computed: {
    isNarrow() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    /** every page the user can open, as they have arranged them; starred and recent visits come from here */
    allSections() {
      return filterNav(applyMenuSettings(navSections, this.menuSettings()), this.pages);
    },
    /** the menu: the pages it can link to, so not the ones that need an id */
    sections() {
      return withoutIdPages(this.allSections);
    },
    /** a role with only a few pages has nothing to rearrange */
    canEdit() {
      return !hasFewPages(this.sections);
    },
    hasChanges() {
      const {moves, sectionNames, sectionSummaries, pageNames, pageSummaries} = this;
      return [moves, sectionNames, sectionSummaries, pageNames, pageSummaries]
        .some(changes => Object.keys(changes).length > 0);
    },
    starredPages() {
      return this.shortcuts(this.starred);
    },
    /** recent visits, less the page you are on and the starred ones, which are listed already */
    recentPages() {
      return this.shortcuts(this.recent)
        .filter(({page}) => !isStarred([this.currentPage, ...this.starred], page));
    },
    currentPage() {
      return this.pageOf(this.$route);
    },
    movingTitle() {
      return this.moving && pageTitle(this.moving);
    },
    targets() {
      return this.moving ? moveTargets(this.sections, this.moving.name) : [];
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        const page = this.pageOf(route);
        if (page) {
          this.recent = rememberPage(this.recent, page);
          this.save();
        }
      },
    },
    // on the page's name, not the route: pages keep their filters in the query as they load
    '$route.name'() {
      this.$emit('input', false);
    },
  },
  methods: {
    /** clicking outside or escape closes the panel, but not while one of its dialogs is open */
    close() {
      if (!this.moving && !this.editingText) {
        this.$emit('input', false);
      }
    },
    closeOnEscape(event) {
      if (event.key === 'Escape' && this.value) {
        this.close();
      }
    },
    settings() {
      const {starred, recent} = this;
      return {...this.menuSettings(), starred, recent};
    },
    /** the settings that arrange the menu (see applyMenuSettings) */
    menuSettings() {
      const {moves, sectionNames, sectionSummaries, pageNames, pageSummaries, customSections} = this;
      return {moves, sectionNames, sectionSummaries, pageNames, pageSummaries, customSections};
    },
    save() {
      // a section of the user's own goes once nothing is moved into it
      const targets = Object.values(this.moves);
      this.customSections = this.customSections.filter(section => targets.includes(`section:${section.id}`));
      const sectionIds = [...navSections.map(sectionId), ...this.customSections.map(section => section.id)];
      const ofSections = changes => Object.fromEntries(Object.entries(changes).filter(([id]) => sectionIds.includes(id)));
      this.sectionNames = ofSections(this.sectionNames);
      this.sectionSummaries = ofSections(this.sectionSummaries);
      saveNavSettings(this.settings());
    },
    /** a route as a starred or recent page: its params only matter for a page with an id in its path */
    pageOf(route) {
      const item = findItem(navSections, route.name);
      return item && {name: route.name, params: item.needs ? {...route.params} : {}};
    },
    /** starred or recent pages that are still pages, linking back to the record for a page with an id */
    shortcuts(pages) {
      return pages
        .map(page => ({page, item: findItem(this.allSections, page.name)}))
        .filter(({item}) => item)
        .map(({page, item}) => ({page, item, to: item.needs ? {name: item.name, params: page.params} : this.location(item)}));
    },
    star(page) {
      this.starred = toggleStar(this.starred, page);
      this.save();
    },
    startMove(item) {
      this.moving = item;
      this.moveTarget = null;
      this.newSectionTitle = '';
    },
    saveMove() {
      let target = this.moveTarget;
      const title = this.newSectionTitle.trim();
      if (title) {
        const id = `custom:${Date.now()}`;
        this.customSections = [...this.customSections, {id, title}];
        target = `section:${id}`;
      }
      // delete first so the latest move is applied last
      const {[this.moving.name]: previous, ...others} = this.moves; // eslint-disable-line no-unused-vars
      this.moves = {...others, [this.moving.name]: target};
      this.moving = null;
      this.save();
    },
    putBack() {
      const {[this.moving.name]: previous, ...others} = this.moves; // eslint-disable-line no-unused-vars
      this.moves = others;
      this.moving = null;
      this.save();
    },
    startEditSection(section) {
      this.startEditText({kind: 'section', key: section.id, title: section.title}, section.summary);
    },
    startEditPage(item) {
      this.startEditText({kind: 'page', key: item.name, title: pageTitle(item)}, item.summary);
    },
    startEditText(editing, summary) {
      this.editingText = editing;
      this.editedTitle = editing.title;
      this.editedSummary = summary;
    },
    /** an empty name or description goes back to how it started, and so does an unchanged one */
    saveText() {
      const {kind, key} = this.editingText;
      const started = applyMenuSettings(navSections, {customSections: this.customSections, moves: this.moves});
      const original = kind === 'section' ? started.find(section => section.id === key) : findItem(started, key);
      const startedTitle = pageTitle(original);
      const startedSummary = original.summary;
      const withChange = (changes, text, startedAs) => {
        const {[key]: previous, ...others} = changes; // eslint-disable-line no-unused-vars
        return text.trim() && text.trim() !== startedAs ? {...others, [key]: text.trim()} : others;
      };
      if (kind === 'section') {
        this.sectionNames = withChange(this.sectionNames, this.editedTitle, startedTitle);
        this.sectionSummaries = withChange(this.sectionSummaries, this.editedSummary, startedSummary);
      } else {
        this.pageNames = withChange(this.pageNames, this.editedTitle, startedTitle);
        this.pageSummaries = withChange(this.pageSummaries, this.editedSummary, startedSummary);
      }
      this.editingText = null;
      this.save();
    },
    resetMenu() {
      if (confirm('Put every page and section back as it started, with their first names? Your stars and recent pages stay.')) {
        this.moves = {};
        this.sectionNames = {};
        this.sectionSummaries = {};
        this.pageNames = {};
        this.pageSummaries = {};
        this.customSections = [];
        this.save();
      }
    },
    /** the settings, plus the menu they make, as names, so a layout can be copied into @/nav */
    downloadSettings() {
      const outline = items => items.map(item => (item.children || []).length
        ? {name: item.name, children: outline(item.children)}
        : item.name);
      const settings = {
        ...this.settings(),
        menu: this.sections.map(section => ({section: section.title, pages: outline(section.items)})),
      };
      downloadBlob(JSON.stringify(settings, null, 2), 'kitchen-menu-settings.json', 'application/json');
    },
  },
};
</script>

<style scoped>
/* above the app bar, below the dialogs */
.nav-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
}

.nav-panel {
  position: fixed;
  top: 56px;
  left: 12px;
  z-index: 101;
}
</style>
